import { z } from 'zod';

import { LOYALTY_CODE, uniqueCodes } from './loyalty_codes';

/** `YYYY-MM-DD` puro — o dia nomeado é o próprio valor. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
/** Instante ISO com fuso EXPLÍCITO (`Z` ou `±HH:MM`) — o dia UTC é o valor. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * Date-only field of the profile wire (`YYYY-MM-DD`, or an ISO instant whose
 * UTC calendar day is what matters). `z.coerce.date()` alone is NOT enough:
 * the JS `Date` parser is far wider than the wire and every extra format ends
 * up persisted as a different day than the one written.
 *   - impossible day rolls over in silence (`2024-04-31` → `2024-05-01`);
 *   - legacy/locale formats parse (`04/30/2024`, `April 30, 2024`);
 *   - an offsetless timestamp (`2024-04-30T21:00:00`) is read in the HOST time
 *     zone, so the same payload means different days on different machines.
 * The value reaches the legacy and the emission through `toISOString()`, so
 * only the two anchored formats above are accepted, and the `YYYY-MM-DD` the
 * string names must be a real day (checked on the date part, not on the
 * instant: with an explicit offset the UTC day legitimately differs). A `Date`
 * instance passes when valid. Output stays `Date`, so consumers' types are
 * unchanged.
 */
export const CalendarDateSchema = z.union([z.date(), z.string()]).transform((raw, ctx) => {
  const date = raw instanceof Date ? raw : new Date(raw);
  const invalid =
    Number.isNaN(date.getTime()) ||
    (typeof raw === 'string' &&
      (!(DATE_ONLY.test(raw) || ISO_INSTANT.test(raw)) || !diaReal(raw.slice(0, 10))));
  if (invalid) {
    ctx.addIssue({ code: 'custom', message: 'Invalid calendar date' });
    return z.NEVER;
  }
  return date;
});

/** `2024-02-30` só é "parseável" porque o `Date` rola o dia: exija round-trip. */
function diaReal(ymd: string): boolean {
  return new Date(`${ymd}T00:00:00.000Z`).toISOString().slice(0, 10) === ymd;
}

/**
 * Write body of `PATCH /profile` (app-integration): the traveler-editable slice
 * of the rich profile (`CustomerProfileViewSchema`). The profile's single truth
 * is the LEGACY `business.users`, written through `businessUpdateUserProfile`
 * (the same mutation the Flutter "Meu perfil" uses); customer-api reflects it
 * via the CDC mirror plus a write-through of this same patch.
 *
 * `.strict()` on purpose, at the top level AND inside every list row: an
 * unknown key (a typo, or a field without a write path) is a 400 at the
 * boundary, never a silently-dropped field. `gender` stays OUT by product
 * decision (#2639): the legacy input has it `@deprecated` (the reservation
 * fills it), and the profile screen no longer shows it.
 *
 * Key semantics:
 *   - key ABSENT from the patch → leave the field alone.
 *   - scalar `null`             → explicit clear. Legacy-imposed exception:
 *                                 `nameCivil` cannot be cleared — the
 *                                 business-service falls back to the stored
 *                                 name on an empty value, so `null` is rejected
 *                                 here rather than accepted and ignored.
 *   - list `[]`                 → explicit empty list (replaces the whole list).
 *
 * `emergencyContacts.phone` / `visas.code` are OPTIONAL (not `.min(1)`-required):
 * a mirrored row can lack that subfield, and re-sending its list unchanged (a
 * round-trip, not a new edit) must not 400 — `name`/`country` stay required so a
 * row is still identifiable, and the client's empty-row-drop rule is what keeps
 * genuinely junk rows out before they ever reach this schema.
 */
export const TravelerProfilePatchSchema = z
  .object({
    preferrerName: z.string().max(120).nullable().optional(),
    tsaPreCheck: z.string().max(40).nullable().optional(),
    emergencyContacts: z
      .array(
        z
          .object({
            name: z.string().min(1).max(120),
            countryCode: z.string().max(8).optional(),
            phone: z.string().max(40).optional(),
          })
          .strict(),
      )
      .max(10)
      .optional(),
    visas: z
      .array(
        z
          .object({
            country: z.string().min(1).max(80),
            code: z.string().max(40).optional(),
            validUntil: CalendarDateSchema.nullable().optional(),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    vaccines: z
      .array(
        z
          .object({
            name: z.string().min(1).max(80),
            date: CalendarDateSchema.nullable().optional(),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    nameCivil: z.string().min(1).max(120).optional(),
    birthdate: CalendarDateSchema.nullable().optional(),
    phone: z.string().max(40).nullable().optional(),
    countryCode: z.string().max(8).nullable().optional(),
    // Picked from the voll-places countries catalog (`code` = ISO alpha-2, the
    // same `{ code, name }` pair the legacy stores and the Flutter app sends).
    nationality: z
      .object({ code: z.string().regex(/^[A-Z]{2}$/), name: z.string().min(1).max(120) })
      .strict()
      .nullable()
      .optional(),
    // Name as printed on tickets (the legacy's `boardingName { userName,
    // lastName }`, `firstName` in domain terms). `null` clears the pair.
    boardingName: z
      .object({ firstName: z.string().max(80).optional(), lastName: z.string().max(80).optional() })
      .strict()
      .nullable()
      .optional(),
    // Mileage programs (a list, replaced whole like the other lists). `airCode`
    // is the 2-char upper-case IATA airline code (`LOYALTY_CODE`) the flight
    // catalog answers (`flightAirlines`); `airCompany` is its display name,
    // carried along like `nationality.name`.
    mileagePlans: z
      .array(
        z
          .object({
            airCode: z.string().regex(LOYALTY_CODE),
            airCompany: z.string().max(80).optional(),
            planCode: z.string().min(1).max(40),
            firstName: z.string().max(80).optional(),
            lastName: z.string().max(80).optional(),
          })
          .strict(),
      )
      .max(20)
      // Código único por linha: duas linhas com o mesmo `airCode` são
      // ambíguas (qual número mandar para a Sabre?) — 400 na borda, nunca
      // "o último vence".
      .superRefine(uniqueCodes((row) => row.airCode, 'airCode'))
      .optional(),
    // Tier-3 documents (CPF, RG, DNI, passaporte). The RAW value only travels
    // traveler → BFF → legacy (plus the mirror write-through); the profile view
    // keeps serving masks, and the client seeds these from the audited
    // `GET /profile/documents` read. `documents` replaces the WHOLE sub-object,
    // the way the legacy stores it — the client sends every field it knows.
    document: z.string().max(20).nullable().optional(),
    hasNoCPF: z.boolean().optional(),
    documents: z
      .object({
        rg: z.string().max(20).nullable().optional(),
        dni: z.string().max(20).nullable().optional(),
        passport: z.string().max(20).nullable().optional(),
        // Free-form on purpose (unlike `nationality.code`): the sub-object is
        // re-sent whole, so whatever the legacy already stores here (`br`,
        // `BRA`, a name) must round-trip without a 400. New picks come from
        // the catalog as ISO alpha-2.
        country: z.string().max(80).nullable().optional(),
        validate: CalendarDateSchema.nullable().optional(),
        dateEmited: CalendarDateSchema.nullable().optional(),
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();
export type TravelerProfilePatch = z.infer<typeof TravelerProfilePatchSchema>;
